import React, { useState, useMemo } from 'react';
import {
  Box, Card, Chip, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TextField,
  Typography, Button,
} from '@mui/material';
import { ReceiptLong } from '@mui/icons-material';
import intl from 'components/i18n/ReactIntlWrapper';
import { getUserEntity } from 'common/UserEntityProvider';
import { handleAPIError } from 'common/errorHandler';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { useApi } from 'api/ApiProvider';
import { setApiKeyToObject } from 'axiosApi/common';
import Title from 'components/common/Title';

// ---- types ----

interface UnreconciledRow {
  invoiceId: number;
  number: string;
  date: string;
  amount: number;
  allocatedAmount: number;
  remainingAmount: number;
  reconciliationStatus: string;
  currencyCode: string;
  from: string;
  to: string;
}

type SortField = 'number' | 'date' | 'from' | 'to' | 'amount' | 'allocatedAmount' | 'remainingAmount' | 'reconciliationStatus';

interface Props {
  /** 'ar' = Paid invoices (credit side), 'ap' = accounts-payable invoices (debit side) */
  mode?: 'ar' | 'ap';
}

// ---- helpers ----

const fmt = (amount: number, code = '') => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code || 'USD' }).format(amount);
  } catch {
    return `${code} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }
};

const today = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

// ---- component ----

const UnreconciledInvoicesReportComponent: React.FC<Props> = ({ mode = 'ar' }) => {
  const api = useApi();
  const userEntity = getUserEntity();
  const toast = useToastMessageQueue();
  const [errors, setErrors] = useState({});

  const [dateFrom, setDateFrom] = useState<string>(firstOfMonth());
  const [dateTo, setDateTo] = useState<string>(today());
  const [rows, setRows] = useState<UnreconciledRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const [sortBy, setSortBy] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: SortField) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };

  const fetchData = async () => {
    const entityId = userEntity?.entityId;
    const entityType = userEntity?.entityType;
    if (!entityId || !entityType) return;

    setLoading(true);
    try {
      const axiosInstance = (api.reportsApi as any).axios;
      const basePath: string = (api.reportsApi as any).basePath;
      const configuration = (api.reportsApi as any).configuration;
      const headers: Record<string, string> = {};
      await setApiKeyToObject(headers, 'Userentity', configuration);
      await setApiKeyToObject(headers, 'Referer', configuration);
      await setApiKeyToObject(headers, 'Authorization', configuration);

      const endpoint = mode === 'ar' ? 'ar' : 'ap';
      const response = await axiosInstance.get(
        `${basePath}/api/v1/UnreconciledInvoiceReport/${endpoint}`,
        { params: { entityId, entityType, dateFrom, dateTo }, headers }
      );

      const data: any[] = response.data?.data ?? [];
      setRows(data.map((r) => ({
        invoiceId: r.invoiceId,
        number: r.number,
        date: r.date,
        amount: r.amount,
        allocatedAmount: r.allocatedAmount,
        remainingAmount: r.remainingAmount,
        reconciliationStatus: r.reconciliationStatus,
        currencyCode: r.currencyCode,
        from: r.from,
        to: r.to,
      })));
      setFetched(true);
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortOrder === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });
  }, [rows, sortBy, sortOrder]);

  // Summary totals grouped by currency
  const byCurrency = useMemo(() => {
    const map: Record<string, { amount: number; remaining: number; count: number; partial: number; none: number }> = {};
    for (const r of rows) {
      const c = r.currencyCode || '?';
      if (!map[c]) map[c] = { amount: 0, remaining: 0, count: 0, partial: 0, none: 0 };
      map[c].amount    += r.amount;
      map[c].remaining += r.remainingAmount;
      map[c].count     += 1;
      if (r.reconciliationStatus === 'Partial') map[c].partial += 1;
      else map[c].none += 1;
    }
    return Object.entries(map);
  }, [rows]);

  const totalCount = rows.length;
  const partialCount = rows.filter((r) => r.reconciliationStatus === 'Partial').length;
  const noneCount = rows.filter((r) => r.reconciliationStatus === 'None').length;

  const title = mode === 'ar'
    ? intl.get('unreconciledReport.ar.title') || 'Unreconciled Invoices (AR)'
    : intl.get('unreconciledReport.ap.title') || 'Unreconciled Account Payables (AP)';

  const colFrom = mode === 'ar'
    ? intl.get('unreconciledReport.column.issuedBy') || 'Issued by'
    : intl.get('unreconciledReport.column.supplier') || 'Supplier';

  const colTo = mode === 'ar'
    ? intl.get('unreconciledReport.column.client') || 'Client'
    : intl.get('unreconciledReport.column.issuedTo') || 'Issued to';

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Title>{title}</Title>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
          <ReceiptLong /> {title}
        </Typography>

        {/* Filters */}
        <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
          <TextField
            label={intl.get('unreconciledReport.filter.from') || 'From'}
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={intl.get('unreconciledReport.filter.to') || 'To'}
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button variant="contained" onClick={fetchData} disabled={loading}>
            {intl.get('unreconciledReport.filter.run') || 'Run Report'}
          </Button>
        </Stack>

        {/* Summary card — one block per currency */}
        {fetched && !loading && rows.length > 0 && (
          <Card variant="outlined" sx={{ mb: 3, bgcolor: 'warning.lighter' }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {intl.get('unreconciledReport.summary.title') || 'Unreconciled Summary'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {intl.get('unreconciledReport.summary.invoices') || 'Invoices'}: {totalCount}
                {' · '}
                {intl.get('reconciliation.partial') || 'Partial'}: {partialCount}
                {' · '}
                {intl.get('unreconciledReport.summary.none') || 'Not started'}: {noneCount}
              </Typography>
              <Stack direction="row" spacing={4} flexWrap="wrap">
                {byCurrency.map(([code, totals]) => (
                  <Box key={code}>
                    <Typography variant="overline" color="text.secondary">{code}</Typography>
                    <Typography variant="h4" color="warning.dark" fontWeight="bold">
                      {fmt(totals.remaining, code)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('unreconciledReport.summary.remaining') || 'Total remaining to reconcile'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('unreconciledReport.summary.total') || 'Total invoice amount'}: {fmt(totals.amount, code)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Card>
        )}

        {/* Skeleton */}
        {loading && <Skeleton variant="rounded" width="100%" height={350} />}

        {/* Empty state */}
        {fetched && !loading && rows.length === 0 && (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            {intl.get('unreconciledReport.empty') || 'No unreconciled invoices found for the selected period.'}
          </Typography>
        )}

        {/* Table */}
        {!loading && rows.length > 0 && (
          <TableContainer component={Box}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'number'}
                      direction={sortBy === 'number' ? sortOrder : 'asc'}
                      onClick={() => handleSort('number')}
                    >
                      <b>{intl.get('invoice.number') || 'Number'}</b>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'date'}
                      direction={sortBy === 'date' ? sortOrder : 'asc'}
                      onClick={() => handleSort('date')}
                    >
                      <b>{intl.get('invoice.date') || 'Date'}</b>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'from'}
                      direction={sortBy === 'from' ? sortOrder : 'asc'}
                      onClick={() => handleSort('from')}
                    >
                      <b>{colFrom}</b>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'to'}
                      direction={sortBy === 'to' ? sortOrder : 'asc'}
                      onClick={() => handleSort('to')}
                    >
                      <b>{colTo}</b>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortBy === 'amount'}
                      direction={sortBy === 'amount' ? sortOrder : 'asc'}
                      onClick={() => handleSort('amount')}
                    >
                      <b>{intl.get('invoice.amount') || 'Amount'}</b>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortBy === 'allocatedAmount'}
                      direction={sortBy === 'allocatedAmount' ? sortOrder : 'asc'}
                      onClick={() => handleSort('allocatedAmount')}
                    >
                      <b>{intl.get('reconciliation.allocatedAmount') || 'Allocated'}</b>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortBy === 'remainingAmount'}
                      direction={sortBy === 'remainingAmount' ? sortOrder : 'asc'}
                      onClick={() => handleSort('remainingAmount')}
                    >
                      <b>{intl.get('unreconciledReport.column.remaining') || 'Remaining'}</b>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <b>{intl.get('unreconciledReport.column.status') || 'Status'}</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow key={row.invoiceId} hover>
                    <TableCell>{row.number}</TableCell>
                    <TableCell>{row.date ? new Date(row.date).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{row.from}</TableCell>
                    <TableCell>{row.to}</TableCell>
                    <TableCell align="right">{fmt(row.amount, row.currencyCode)}</TableCell>
                    <TableCell align="right">{fmt(row.allocatedAmount, row.currencyCode)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                      {fmt(row.remainingAmount, row.currencyCode)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          row.reconciliationStatus === 'Partial'
                            ? intl.get('reconciliation.partial') || 'Partial'
                            : intl.get('unreconciledReport.status.none') || 'None'
                        }
                        color={row.reconciliationStatus === 'Partial' ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Card>
  );
};

export default UnreconciledInvoicesReportComponent;
